import React, {
  memo,
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';

import { Button } from '@material-ui/core';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { Table, Modal } from '~/components';
import { t, canURL } from '~/helpers';

import IOForm from '../IOForm';
import * as S from './styles';

function IOGrid({ data, onCallback, tipoIO, title, idposto }) {
  const [items, setItems] = useState(data || []);
  const [active, setActive] = useState([]);

  const modalRef = useRef(null);

  const CRUD = useMemo(
    () =>
      Object.keys(canURL()).some((url) => ['create', 'update'].includes(url)),
    []
  );

  const alarmesLabels = useMemo(
    () => ({
      1: t('s.configalarme.nivelmuitobaixo'),
      2: t('s.configalarme.nivelbaixo'),
      3: t('s.configalarme.nivelalto'),
      4: t('s.configalarme.nivelmuitoalto'),
      5: t('s.configalarme.falhacomunicacao'),
      6: t('s.configalarme.alteracaomodo'),
      7: t('s.configalarme.perdaproduto'),
      8: t('s.configalarme.nivelagua'),
      9: t('s.configalarme.variacaotumofechado'),
      10: t('s.configalarme.overfillatuado'),
    }),
    []
  );

  useEffect(() => {
    setItems(data || []);
    setActive([]);
    // O pai recria `data` em cada render; resetar por `data` apagaria edicoes locais.
  }, [tipoIO]);

  useEffect(() => {
    onCallback(items);
  }, [items, onCallback]);

  const getModoSegurancaLabel = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '-';

    return Number(value) === 1 ? 'Normalmente fechado' : 'Normalmente aberto';
  }, []);

  const getModoOperacaoLabel = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '-';

    return Number(value) === 1 ? 'Intermitente' : 'Contínuo';
  }, []);

  const getTipoDesativacaoLabel = useCallback((value) => {
    if (value === null || value === undefined || value === '') return '-';

    switch (Number(value)) {
      case 1:
        return 'Na normalização';
      case 2:
        return 'No reconhecimento e na normalização';
      default:
        return 'No reconhecimento';
    }
  }, []);

  const getIdTanque = useCallback(
    (tanque) => Number(tanque?.idtanquefisico || tanque?.value || tanque),
    []
  );

  const getIdConfigAlarmeTanque = useCallback(
    (alarme) => Number(alarme?.idconfigalarmetanque || alarme?.value || alarme),
    []
  );

  const getNomeAlarme = useCallback(
    (alarme) => {
      const idconfigalarmetanque = getIdConfigAlarmeTanque(alarme);

      if (alarme?.nomealarme) return alarme.nomealarme;
      if (alarme?.nomeconfigalarmetanquecombo) {
        return t(alarme.nomeconfigalarmetanquecombo);
      }
      if (alarme?.codigolabelsistema) return t(alarme.codigolabelsistema);

      return (
        alarmesLabels[Number(idconfigalarmetanque)] ||
        String(idconfigalarmetanque || '-')
      );
    },
    [alarmesLabels, getIdConfigAlarmeTanque]
  );

  const cells = useMemo(() => {
    const baseCells = [
      {
        id: 'portaconversorio',
        label: 'IO',
      },
      {
        id: 'nomeconversorio',
        label: 'Nome',
        format: (value) => value || '-',
      },
    ];

    if (tipoIO === 'E') {
      return baseCells;
    }

    return [
      ...baseCells,
      {
        id: 'modosegurancaconversorio',
        label: 'Modo Segurança',
        format: getModoSegurancaLabel,
      },
      {
        id: 'modooperacaoconversorio',
        label: 'Modo Operação',
        format: getModoOperacaoLabel,
      },
      {
        id: 'tipodesativacaoconversorio',
        label: 'Tipo Desativação',
        format: getTipoDesativacaoLabel,
      },
    ];
  }, [
    tipoIO,
    getModoSegurancaLabel,
    getModoOperacaoLabel,
    getTipoDesativacaoLabel,
  ]);

  const detailCells = useMemo(() => {
    if (tipoIO === 'E') {
      return [
        {
          id: 'codigotanquefisico',
          label: t('s.configuracao.tanquefisico.codigotanquefisico'),
        },
        {
          id: 'alarmesFormatados',
          label: 'Alarmes',
        },
      ];
    }

    return [
      {
        id: 'idconfigalarmetanque',
        label: 'Cód. Alarme',
      },
      {
        id: 'nomealarme',
        label: t('s.geral.alertasistema.codigolabelsistema'),
      },
    ];
  }, [tipoIO]);

  const detailItems = useMemo(() => {
    if (isEmpty(active)) return [];

    if (tipoIO === 'E') {
      return (active[0]?.tanques || []).filter(Boolean).map((tanque) => {
        const idtanquefisico = getIdTanque(tanque);
        const alarmes = (tanque.alarmes || [])
          .filter(Boolean)
          .map(getNomeAlarme);

        return {
          ...tanque,
          idtanquefisico,
          codigotanquefisico:
            tanque.codigotanquefisico ||
            tanque.nomeconversorio ||
            tanque.nometanquefisico ||
            String(idtanquefisico || '-'),
          alarmesFormatados: isEmpty(alarmes) ? '-' : alarmes.join(', '),
        };
      });
    }

    return (active[0]?.alarmes || []).filter(Boolean).map((alarme) => ({
      ...alarme,
      nomealarme:
        alarmesLabels[Number(alarme.idconfigalarmetanque)] ||
        String(alarme.idconfigalarmetanque || '-'),
    }));
  }, [active, tipoIO, alarmesLabels, getIdTanque, getNomeAlarme]);

  const handleChangeModifyActive = useCallback((value) => {
    setActive(value);
  }, []);

  const handleOpenModalToUpdateRow = useCallback(() => {
    if (isEmpty(active)) return;

    modalRef.current.handleOpenModal({
      data: {
        ...active[0],
        idposto,
      },
      tipoIO,
    });
  }, [active, idposto, tipoIO]);

  const handleUpdateOrCreateInRow = useCallback(
    (value) => {
      const formData = {
        ...value,
        idposto,
        tipoconversorio: tipoIO,
        portaconversorio: Number(value.portaconversorio),
      };

      const values = [...items];

      const index = values.findIndex(
        (item) =>
          Number(item.portaconversorio) === Number(formData.portaconversorio)
      );

      if (index >= 0) {
        values[index] = formData;
      }

      setItems(values);
      setActive([]);
    },
    [items, idposto, tipoIO]
  );

  return (
    <S.Wrapper>
      <header>
        <h3>{title}</h3>

        {CRUD && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            type="button"
            disabled={isEmpty(active)}
            onClick={handleOpenModalToUpdateRow}
          >
            {t('s.geral.botaoalterar')}
          </Button>
        )}
      </header>

      <S.TablesContainer>
        <Table
          noButton
          showPagination={false}
          paginate={99}
          cells={cells}
          data={items}
          onCallback={handleChangeModifyActive}
          unique="portaconversorio"
          size="small"
        />

        {!isEmpty(active) && (
          <S.Details>
            <h4>
              {tipoIO === 'E'
                ? 'Tanques selecionados'
                : 'Alarmes selecionados'}
            </h4>

            <Table
              noButton
              showPagination={false}
              paginate={99}
              cells={detailCells}
              data={detailItems}
              onCallback={() => {}}
              unique={
                tipoIO === 'E' ? 'idtanquefisico' : 'idconfigalarmetanque'
              }
              size="small"
            />
          </S.Details>
        )}
      </S.TablesContainer>

      <Modal
        form
        disableBackdropClick
        disableEscapeKeyDown
        title={
          tipoIO === 'E' ? 'Configuração IO Entrada' : 'Configuração IO Saída'
        }
        maxWidth={tipoIO === 'E' ? 'md' : 'sm'}
        ref={modalRef}
        onForm={handleUpdateOrCreateInRow}
        component={IOForm}
        tipoIO={tipoIO}
      />
    </S.Wrapper>
  );
}

IOGrid.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  onCallback: PropTypes.func.isRequired,
  idposto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tipoIO: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

IOGrid.defaultProps = {
  data: [],
  idposto: null,
};

export default memo(IOGrid);
